import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TextareaModule } from 'primeng/textarea';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DatepickerFastTypingDirective } from '../../../../../../directives/datepicker-fast-typing.directive';
import { DatepickerSelectOnClickDirective } from '../../../../../../directives/datepicker-select-on-click.directive';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { MeasureDetailService } from '../../../../../../services/measure-detail.service';
import { DetailedMeasureFormula } from '../../../../../../services/api/generated/model';
import { CesDeleteButtonComponent } from '../../../../../ui/ces-buttons';
import { ConfirmationConfig } from '../../../../../ui/ces-buttons/interfaces';
import { CesDialogFooterComponent } from '../../../../../ui/dialog-footer/dialog-footer.component';
import { MeasureFormula } from '../../../../../../types/measure-formula.interface';
import { buildDeleteConfig } from '../../../../../../utils/delete-confirmation';
import { extractServerErrorCode } from '../formula-period';
import { confirmOverwriteIfNeeded, findNextSegmentStartMs } from '../measure-formula.helpers';

export interface SuspendDialogCreateData {
  mode: 'create';
  measureId: number;
  segments: readonly MeasureFormula[];
}

export interface SuspendDialogEditData {
  mode: 'edit';
  measureId: number;
  inactiveId: number;
  startTs: string;
  endTs: string | null;
  comment: string | null;
  segments: readonly MeasureFormula[];
}

export type SuspendDialogData = SuspendDialogCreateData | SuspendDialogEditData;

// Distinctive close result for the in-dialog delete action: never collides with a repainted
// segment (DetailedMeasureFormula always exposes `id`/`startTs`, never `action`), so the caller
// can route to the deletion toast without inspecting the dialog's own mode.
export const SUSPEND_DIALOG_DELETE_MARKER = { action: 'delete' } as const;
export type SuspendDialogDeleteResult = typeof SUSPEND_DIALOG_DELETE_MARKER;

export function isSuspendDialogDeleteResult(value: unknown): value is SuspendDialogDeleteResult {
  return typeof value === 'object' && value !== null && (value as { action?: unknown }).action === 'delete';
}

export interface ValidationError {
  key: string;
  params?: Record<string, string>;
}

const COMMENT_MAX_LENGTH = 500;

const SERVER_ERROR_KEYS: Record<string, string> = {
  SUSPEND_END_BEFORE_START: 'entities.measureFormula.messages.suspendEndBeforeStart',
  SUSPEND_COMMENT_TOO_LONG: 'entities.measureFormula.messages.suspendCommentTooLong',
  SUSPEND_COMMENT_INVALID_TYPE: 'entities.measureFormula.messages.suspendCommentTooLong',
};

@Component({
  selector: 'app-suspend-measure-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesDeleteButtonComponent,
    DatePickerModule,
    FormsModule,
    TextareaModule,
    TranslocoModule,
    CesDialogFooterComponent,
    DatepickerSelectOnClickDirective,
    DatepickerFastTypingDirective,
  ],
  templateUrl: './suspend-measure-dialog.component.html',
})
export class SuspendMeasureDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<SuspendDialogData>>(DynamicDialogConfig);
  private readonly dataService = inject(MeasureDetailService);
  private readonly translate = inject(TranslocoService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);

  protected readonly dialogData = this.requireDialogData();
  protected readonly mode = this.dialogData.mode;
  protected readonly startDate = signal<Date | null>(this.initialStartDate());
  protected readonly endDate = signal<Date | null>(this.initialEndDate());
  protected readonly comment = signal<string>(this.initialComment());
  protected readonly touched = signal(false);
  protected readonly submitting = signal(false);
  protected readonly deleting = signal(false);
  protected readonly serverError = signal<ValidationError | null>(null);
  protected readonly maxCommentLength = COMMENT_MAX_LENGTH;

  protected readonly commentLength = computed(() => this.comment().length);

  // Ordonnancement libre : plus de plancher/plafond calcule sur la formule active ou la
  // suspension voisine (l'API peint le segment sans restriction temporelle).
  protected readonly startPlaceholderKey = 'entities.measureFormula.messages.suspendStartPlaceholder';
  protected readonly startHintKey = 'entities.measureFormula.messages.suspendStartHint';

  protected readonly startDefaultDate = computed<Date>(() => this.midnightToday());
  protected readonly endDefaultDate = computed<Date>(() => this.midnightToday());

  private midnightToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  protected readonly clientValidationError = computed<ValidationError | null>(() => {
    const start = this.startDate();
    const end = this.endDate();
    if (end) {
      const reference = start ?? new Date();
      if (end.getTime() <= reference.getTime()) {
        return { key: 'entities.measureFormula.messages.suspendEndBeforeStart' };
      }
    }
    if (this.comment().length > COMMENT_MAX_LENGTH) {
      return { key: 'entities.measureFormula.messages.suspendCommentTooLong' };
    }
    return null;
  });

  protected readonly visibleError = computed<ValidationError | null>(() => {
    const client = this.touched() ? this.clientValidationError() : null;
    return client ?? this.serverError();
  });

  protected onStartDateChange(date: Date | null): void {
    this.startDate.set(date);
    this.touched.set(true);
    this.serverError.set(null);
  }

  protected onEndDateChange(date: Date | null): void {
    this.endDate.set(date);
    this.touched.set(true);
    this.serverError.set(null);
  }

  protected onCommentChange(value: string): void {
    this.comment.set(value);
    this.serverError.set(null);
  }

  protected cancel(): void {
    if (this.submitting() || this.deleting()) return;
    this.ref.close();
  }

  protected deleteConfirmationConfig(): ConfirmationConfig {
    return {
      ...buildDeleteConfig('entities.measureFormula.confirmations.deleteSuspension'),
      header: 'entities.measureFormula.confirmations.deleteSuspensionTitle',
    };
  }

  // Fires once the ces-delete-button's own confirmation prompt has been accepted. Deletes the
  // segment directly (no repaint to preview: removing an inactive period never overlaps a
  // sibling) and closes with a marker distinct from the create/edit repaint so the caller routes
  // to the deletion toast instead of trying to select a non-existent response id.
  protected async onDeleteConfirmed(): Promise<void> {
    if (this.dialogData.mode !== 'edit' || this.submitting() || this.deleting()) return;
    this.deleting.set(true);
    this.serverError.set(null);
    try {
      await this.dataService.deleteMeasureFormula(this.dialogData.inactiveId);
      this.ref.close(SUSPEND_DIALOG_DELETE_MARKER);
    } catch {
      this.serverError.set({ key: 'entities.measureFormula.messages.deleteSuspensionError' });
      this.deleting.set(false);
    }
  }

  protected async confirm(): Promise<void> {
    this.touched.set(true);
    if (this.clientValidationError() || this.submitting() || this.deleting()) return;

    if (this.dialogData.mode === 'create' && !(await this.confirmOverwriteImpact(this.dialogData))) {
      return;
    }
    if (this.dialogData.mode === 'edit' && !(await this.confirmEditOverwriteImpact(this.dialogData))) {
      return;
    }

    this.submitting.set(true);
    this.serverError.set(null);
    try {
      if (this.dialogData.mode === 'create') {
        const response = await this.submitCreate();
        this.ref.close(response);
      } else {
        const response = await this.submitEdit(this.dialogData);
        this.ref.close(response);
      }
    } catch (err) {
      this.serverError.set(this.mapServerError(err));
      this.submitting.set(false);
    }
  }

  // Previews the painter's cascade (truncated/split/swallowed siblings) before the segment is
  // actually created, so the user can back out without an API call. An absent end date caps at
  // the next surviving segment (the API never truly paints to infinity in that case), matching
  // ``insert_segment``'s bounding of an open-ended suspension.
  private async confirmOverwriteImpact(data: SuspendDialogCreateData): Promise<boolean> {
    const start = this.startDate() ?? new Date();
    const end = this.endDate();
    const endMs = end ? end.getTime() : findNextSegmentStartMs(data.segments, start.getTime());
    return this.askOverwriteConfirmation(data.segments, start.getTime(), endMs);
  }

  // Same gate as creation, but for a resize: the segment being edited is excluded from the
  // sibling list (it isn't a conflict with itself) before applying the same bounding rule.
  private async confirmEditOverwriteImpact(data: SuspendDialogEditData): Promise<boolean> {
    const siblings = data.segments.filter(s => s.id !== data.inactiveId);
    const start = this.startDate() ?? new Date();
    const end = this.endDate();
    const endMs = end ? end.getTime() : findNextSegmentStartMs(siblings, start.getTime());
    return this.askOverwriteConfirmation(siblings, start.getTime(), endMs);
  }

  private async askOverwriteConfirmation(
    segments: readonly MeasureFormula[],
    startMs: number,
    endMs: number | null,
  ): Promise<boolean> {
    return confirmOverwriteIfNeeded(
      {
        ask: (messageKey, headerKey, params, icon, acceptLabelKey) =>
          this.confirmationHelper.ask(messageKey, headerKey, params, icon, acceptLabelKey),
        translate: (key, params) => this.translate.translate(key, params),
      },
      segments,
      startMs,
      endMs,
    );
  }

  private async submitCreate(): Promise<DetailedMeasureFormula> {
    const start = this.startDate();
    const end = this.endDate();
    const trimmed = this.comment().trim();
    const payload = {
      ...(start ? { startTs: start.toISOString() } : {}),
      ...(end ? { endTs: end.toISOString() } : {}),
      ...(trimmed ? { comment: trimmed } : {}),
    };
    return this.dataService.suspendMeasure(this.dialogData.measureId, payload);
  }

  // Retourne le segment issu de la repeinture serveur : son id remplace celui de la
  // suspension editee (le backend supprime l'ancienne ligne et en insere une nouvelle).
  private async submitEdit(data: SuspendDialogEditData): Promise<MeasureFormula | null> {
    const start = this.startDate();
    const end = this.endDate();
    const trimmed = this.comment().trim();
    const payload: Record<string, string | null> = {};
    payload['startTs'] = start ? start.toISOString() : null;
    payload['endTs'] = end ? end.toISOString() : null;
    payload['suspendComment'] = trimmed.length > 0 ? trimmed : null;
    return this.dataService.updateMeasureFormula(data.inactiveId, payload);
  }

  private mapServerError(err: unknown): ValidationError {
    const code = extractServerErrorCode(err);
    if (code && SERVER_ERROR_KEYS[code]) {
      return { key: SERVER_ERROR_KEYS[code] };
    }
    return { key: 'entities.measureFormula.messages.suspendError' };
  }

  private requireDialogData(): SuspendDialogData {
    const data = this.config.data;
    if (!data) throw new Error('SuspendMeasureDialogComponent requires DynamicDialogConfig.data');
    return data;
  }

  private initialStartDate(): Date | null {
    if (this.dialogData.mode === 'edit') return new Date(this.dialogData.startTs);
    return null;
  }

  private initialEndDate(): Date | null {
    if (this.dialogData.mode === 'edit' && this.dialogData.endTs) {
      return new Date(this.dialogData.endTs);
    }
    return null;
  }

  private initialComment(): string {
    if (this.dialogData.mode !== 'edit') return '';
    return this.dialogData.comment ?? '';
  }
}
